import React, { useState } from 'react';
import {
  Bell, Check, X, CreditCard, Banknote, Smartphone, Clock,
  ExternalLink, ChevronRight, AlertCircle, Utensils
} from 'lucide-react';
import { useServiceRequests, type ServiceRequestItem } from '../../hooks/useServiceRequests';
import type { Table } from '../../types/@types/table';

interface ServiceRequestsDrawerProps {
  onSelectTable?: (tableId: number | string) => void;
  className?: string;
}

function timeAgo(dateStr: string) {
  const diffSecs = Math.max(0, Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000));
  if (diffSecs < 60) return `${diffSecs}s`;
  const diffMins = Math.floor(diffSecs / 60);
  if (diffMins < 60) return `${diffMins}m`;
  return `${Math.floor(diffMins / 60)}h`;
}

export const ServiceRequestsDrawer: React.FC<ServiceRequestsDrawerProps> = ({
  onSelectTable,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { requests, activeCount, attendRequest, isAttending } = useServiceRequests();

  const handleAttend = async (e: React.MouseEvent, reqId: number) => {
    e.stopPropagation();
    await attendRequest(reqId);
  };

  const handleGoToTable = (req: ServiceRequestItem) => {
    if (onSelectTable) {
      onSelectTable(req.table_id);
      setIsOpen(false);
    }
  };

  return (
    <>
      {/* ── Trigger Floating Bell Button ── */}
      <div className={`fixed bottom-20 right-4 z-40 sm:bottom-6 sm:right-6 ${className}`}>
        <button
          type="button"
          onClick={() => setIsOpen(prev => !prev)}
          className={`group relative flex items-center gap-2.5 rounded-full px-4 py-3 shadow-2xl transition-all duration-300 active:scale-95 ${
            activeCount > 0
              ? 'bg-gradient-to-r from-amber-500 to-rose-500 text-white ring-4 ring-amber-400/40 animate-[pulse_2.5s_infinite]'
              : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 dark:bg-neutral-800 dark:text-neutral-200 dark:border-neutral-700'
          }`}
          aria-label="Bandeja de solicitudes de comensales"
        >
          <Bell
            size={22}
            className={activeCount > 0 ? 'animate-[wiggle_1s_ease-in-out_infinite]' : 'text-slate-500 dark:text-neutral-400'}
          />
          <span className="text-xs font-black tracking-tight">
            {activeCount > 0 ? (
              <span>{activeCount} {activeCount === 1 ? 'Llamada' : 'Llamadas'}</span>
            ) : (
              <span className="hidden sm:inline">Timbre Mesero</span>
            )}
          </span>

          {activeCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-600 text-[10px] font-black text-white ring-2 ring-white">
              {activeCount}
            </span>
          )}
        </button>
      </div>

      {/* ── Drawer Modal / Flyout ── */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-end sm:p-4 bg-black/40 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="w-full sm:max-w-md h-[85dvh] sm:h-[80dvh] rounded-t-3xl sm:rounded-3xl bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-6 sm:slide-in-from-right-6 duration-200">
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-slate-100 dark:border-neutral-800 shrink-0 bg-slate-50/50 dark:bg-neutral-900/50">
              <div className="flex items-center gap-2.5">
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400">
                  <Bell size={20} />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-800 dark:text-white leading-tight">
                    Solicitudes de Comensales
                  </h3>
                  <p className="text-xs font-medium text-slate-500 dark:text-neutral-400">
                    {activeCount > 0 ? `${activeCount} pendiente(s) por atender` : 'Sin solicitudes pendientes'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-neutral-800 dark:hover:text-white transition"
              >
                <X size={18} />
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {requests.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-6 text-slate-400 dark:text-neutral-500">
                  <div className="h-16 w-16 rounded-full bg-slate-100 dark:bg-neutral-800 flex items-center justify-center mb-3">
                    <Check size={28} className="text-emerald-500" />
                  </div>
                  <p className="font-bold text-sm text-slate-700 dark:text-neutral-300">
                    ¡Todo al día!
                  </p>
                  <p className="text-xs text-slate-500 dark:text-neutral-400 mt-1 max-w-xs">
                    No hay mesas llamando ni pidiendo cuenta en este momento.
                  </p>
                </div>
              ) : (
                requests.map(req => {
                  const isCall = req.type === 'CALL_WAITER';
                  const tableNumber = req.table?.number ?? req.table_id;

                  return (
                    <div
                      key={req.id}
                      className="group relative rounded-2xl border border-slate-200/90 dark:border-neutral-800 bg-white dark:bg-neutral-850 p-4 shadow-xs transition hover:shadow-md hover:border-amber-400/50"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-white dark:bg-white dark:text-neutral-900 font-black text-sm">
                            #{tableNumber}
                          </span>
                          <div>
                            <span
                              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                                isCall
                                  ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300'
                                  : 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                              }`}
                            >
                              {isCall ? '🙋‍♂️ Llama Mesero' : '💳 Pide Cuenta'}
                            </span>
                            <p className="text-xs font-semibold text-slate-700 dark:text-neutral-200 mt-0.5">
                              Mesa {tableNumber} {req.table?.name ? `(${req.table.name})` : ''}
                            </p>
                          </div>
                        </div>

                        <span className="flex items-center gap-1 text-[11px] font-bold text-slate-400 dark:text-neutral-500 tabular-nums">
                          <Clock size={12} />
                          {timeAgo(req.created_at)}
                        </span>
                      </div>

                      {/* Payment method badge if requesting bill */}
                      {!isCall && (
                        <div className="mt-2.5 flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-neutral-300">
                          <span className="text-[11px] text-slate-400">Medio de pago:</span>
                          <span className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 dark:bg-neutral-800 px-2.5 py-1 text-slate-800 dark:text-neutral-200 font-bold">
                            {req.payment_method === 'CASH' && <><Banknote size={14} className="text-emerald-600" /> Efectivo</>}
                            {req.payment_method === 'CARD' && <><CreditCard size={14} className="text-blue-600" /> Datáfono / Tarjeta</>}
                            {req.payment_method === 'TRANSFER' && <><Smartphone size={14} className="text-indigo-600" /> Transferencia</>}
                            {!req.payment_method && 'No especificado'}
                          </span>
                        </div>
                      )}

                      {/* Customer Note */}
                      {req.notes && (
                        <div className="mt-2 rounded-xl bg-slate-50 dark:bg-neutral-800/60 p-2.5 text-xs text-slate-700 dark:text-neutral-300 italic border border-slate-100 dark:border-neutral-800">
                          "{req.notes}"
                        </div>
                      )}

                      {/* Actions */}
                      <div className="mt-3.5 flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-neutral-800/80">
                        {onSelectTable && (
                          <button
                            type="button"
                            onClick={() => handleGoToTable(req)}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 py-2 px-3 text-xs font-bold text-slate-700 dark:text-neutral-200 hover:bg-slate-50 dark:hover:bg-neutral-700 transition active:scale-95"
                          >
                            <span>Ir a Mesa</span>
                            <ExternalLink size={13} />
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={isAttending}
                          onClick={e => handleAttend(e, req.id)}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 py-2 px-3 text-xs font-bold text-white shadow-xs transition active:scale-95 disabled:opacity-50"
                        >
                          <Check size={14} />
                          <span>Atender ✓</span>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ServiceRequestsDrawer;
